import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

interface TrainInfo {
  commuterLineID: string;
  runningCurrently: boolean;
  cancelled: string;
  departureDate: string;
  trainNumber: number;
  trainType: string;
  operatorShortCode?: string;
  timetableType?: string;
  timeTableRows: TimeTableRow[];
}

interface TimeTableRow {
  stationShortCode: string;
  type: "ARRIVAL" | "DEPARTURE";
  scheduledTime: string;
  actualTime?: string;
  commercialStop: boolean;
  commercialTrack: string;
}

interface StationMetadata {
  stationShortCode: string;
  stationName: string;
  longitude: number;
  latitude: number;
}

const TrainHistoryView: React.FC = () => {
  const { trainNumber } = useParams<{ trainNumber: string }>();
  const trainNumberNum = trainNumber ? Number(trainNumber) : null;

  const [trainInfo, setTrainInfo] = useState<TrainInfo | null>(null);
  const [stations, setStations] = useState<StationMetadata[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rowRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const [formattedDate, setFormattedDate] = useState<string>("");

  const departureDate = trainInfo?.departureDate;
  const operator = trainInfo?.operatorShortCode;
  const timetableType = trainInfo?.timetableType;

  const formattedDepartureDate = departureDate
    ? new Date(departureDate).toLocaleDateString("fi-FI", {
        weekday: "long",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  useEffect(() => {
    const date = new Date();
    const weekdays = [
      "Sunnuntai",
      "Maanantai",
      "Tiistai",
      "Keskiviikko",
      "Torstai",
      "Perjantai",
      "Lauantai",
    ];
    const weekday = weekdays[date.getDay()];

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    setFormattedDate(`${weekday} ${day}.${month}.${year}`);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [trainRes, stationRes] = await Promise.all([
        fetch(
          `https://rata.digitraffic.fi/api/v1/trains/latest/${trainNumberNum}`,
        ),
        fetch(`https://rata.digitraffic.fi/api/v1/metadata/stations`),
      ]);

      if (!trainRes.ok || !stationRes.ok) throw new Error("API error");

      const trainData: TrainInfo[] = await trainRes.json();
      const stationData: StationMetadata[] = await stationRes.json();

      setTrainInfo(trainData?.[0] ?? null);
      setStations(stationData);
    } catch (e: any) {
      setError(e.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!trainNumberNum) return;
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [trainNumberNum]);

  const stationMap = useMemo(() => {
    return new Map(stations.map((s) => [s.stationShortCode, s]));
  }, [stations]);

  const grouped = useMemo(() => {
    if (!trainInfo) return new Map<string, TimeTableRow[]>();

    const map = new Map<string, TimeTableRow[]>();

    for (const row of trainInfo.timeTableRows) {
      if (!map.has(row.stationShortCode)) {
        map.set(row.stationShortCode, []);
      }
      map.get(row.stationShortCode)!.push(row);
    }

    return map;
  }, [trainInfo]);

  const arrivalList = Array.from(grouped.entries()).map(
    ([stationCode, rows]) => {
      const arrival = rows.find((r) => r.type === "ARRIVAL");
      return {
        stationCode,
        arrivalTime: arrival?.actualTime ?? null,
      };
    },
  );

  const lastCompletedIndex = (() => {
    let last = -1;

    arrivalList.forEach((s, i) => {
      if (s.arrivalTime) {
        last = i;
      }
    });

    return last;
  })();

  const lastActualEvent = (() => {
    const entries = Array.from(grouped.entries());

    let latest: {
      stationCode: string;
      diff: number;
      actualTime: number;
    } | null = null;

    for (const [stationCode, rows] of entries) {
      for (const r of rows) {
        if (!r.actualTime || !r.scheduledTime) continue;

        const actual = new Date(r.actualTime).getTime();
        const scheduled = new Date(r.scheduledTime).getTime();

        const diff = Math.floor((actual - scheduled) / 60000);

        if (!latest || actual > latest.actualTime) {
          latest = {
            stationCode,
            diff,
            actualTime: actual,
          };
        }
      }
    }

    return latest;
  })();

  const lastArrivalDiff = lastActualEvent?.diff ?? null;
  const lastArrivalIndex = lastActualEvent
    ? Array.from(grouped.keys()).indexOf(lastActualEvent.stationCode)
    : -1;

  const TimeRow = ({
    label,
    scheduled,
    actual,
    track,
  }: {
    label?: React.ReactNode;
    scheduled?: string;
    actual?: string;
    track?: string;
  }) => {
    if (!scheduled) return null;

    const late =
      actual &&
      Math.floor(new Date(actual).getTime() / 60000) >
        Math.floor(new Date(scheduled).getTime() / 60000);

    return (
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <div
          style={{
            display: "flex",
            gap: 10,
            width: 75,
          }}
        >
          <div>
            {new Date(scheduled).toLocaleTimeString("fi-FI", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>

          <div
            style={{
              color: !actual ? "#444" : late ? "rgb(211,130,146)" : "#2ecc71",
            }}
          >
            {actual
              ? new Date(actual).toLocaleTimeString("fi-FI", {
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : "--.--"}
          </div>
        </div>
        {label && (
          <div
            style={{
              fontWeight: 600,
              textAlign: "left",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </div>
        )}
        {track && (
          <div
            className="raide-circle"
            style={{
              marginLeft: "auto",
            }}
          >{`r${track}`}</div>
        )}
      </div>
    );
  };

  const { origin, destination } = useMemo(() => {
    if (!trainInfo?.timeTableRows?.length)
      return { origin: null, destination: null };

    const rows = [...trainInfo.timeTableRows]
      .filter((r) => r.commercialStop)
      .sort(
        (a, b) =>
          new Date(a.scheduledTime).getTime() -
          new Date(b.scheduledTime).getTime(),
      );

    const origin = rows.find((r) => r.type === "DEPARTURE");
    const destination = [...rows].reverse().find((r) => r.type === "ARRIVAL");

    return {
      origin,
      destination,
    };
  }, [trainInfo]);

  useEffect(() => {
    if (!trainInfo) return;

    const entries = Array.from(grouped.entries());

    let lastIndex = -1;

    entries.forEach(([_, rows], i) => {
      const arrival = rows.find((r) => r.type === "ARRIVAL");
      if (arrival?.actualTime) {
        lastIndex = i;
      }
    });

    if (lastIndex >= 0) {
      const stationCode = entries[lastIndex][0];
      const el = rowRefs.current[stationCode];

      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [trainInfo, grouped]);

  if (loading && !trainInfo) {
    return (
      <div style={{ padding: 20, color: "white" }}>
        <div className="loader" />
      </div>
    );
  }

  if (!loading && !trainInfo) {
    return (
      <div
        style={{
          padding: 20,
          color: "lightgray",
          textAlign: "center",
          marginTop: 80,
          marginLeft: 50,
        }}
      >
        <div style={{ fontSize: 24, color: "gray" }}>{trainNumber}</div>
        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>
          Junaa ei löytynyt
        </div>

        <div style={{ fontSize: 14, color: "gray" }}>
          Tarkista junanumero tai yritä myöhemmin uudelleen.
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 20,
        background: "transparent",
        color: "white",
        marginBottom: "120px",
      }}
    >
      <div
        style={{
          marginLeft: 40,
          padding: 20,
          background: "#032938",
          maxWidth: 280,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 4,
          }}
        >
          <div style={{ fontSize: 13, color: "#aaa" }}>Juna</div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            {trainInfo?.trainType} {trainInfo?.trainNumber}
          </div>
        </div>

        {trainInfo?.commuterLineID && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
            }}
          >
            <div style={{ fontSize: 13, color: "#aaa" }}>Junatunnus</div>
            <div style={{ fontSize: 13, color: "white" }}>
              {trainInfo?.commuterLineID || "-"}
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 4,
          }}
        >
          <div style={{ fontSize: 13, color: "#aaa" }}>Tila</div>
          <div
            style={{
              fontSize: 13,
              color: trainInfo?.cancelled
                ? "rgb(211,130,146)"
                : trainInfo?.runningCurrently
                  ? "turquoise"
                  : "#aaa",
            }}
          >
            {trainInfo?.cancelled
              ? "Peruttu"
              : trainInfo?.runningCurrently
                ? "Liikennöinnissä"
                : "Ei tietoa"}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 4,
          }}
        >
          <div style={{ fontSize: 13, color: "#aaa" }}>Lähtöpäivä</div>
          <div style={{ fontSize: 13, color: "white" }}>
            {formattedDepartureDate}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: 4,
          }}
        >
          <div style={{ fontSize: 13, color: "#aaa" }}>Operaattori</div>
          <div style={{ fontSize: 13, color: "#bbb" }}>
            {operator} · {timetableType}
          </div>
        </div>

        {origin && destination && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
            }}
          >
            <div style={{ fontSize: 13, color: "#aaa" }}>Reitti</div>
            <div style={{ fontSize: 13, color: "white" }}>
              {origin.stationShortCode} → {destination.stationShortCode}
            </div>
          </div>
        )}
      </div>
      <div
        className="ms-3 mb-1"
        style={{
          position: "fixed",
          left: "-10px",
          top: 58,
          color: "#aaa",
          fontSize: 18,
          writingMode: "vertical-rl",
        }}
      >
        {trainInfo && (
          <div>
            <span
              style={{
                color: "white",
                paddingBottom: 32,
                fontWeight: 700,
                fontSize: 28,
              }}
            >
              {trainInfo.trainType} {trainInfo.trainNumber}
            </span>
            {origin && destination && (
              <span
                style={{
                  paddingTop: 20,
                }}
              >
                {origin.stationShortCode} → {destination.stationShortCode}
              </span>
            )}
          </div>
        )}
      </div>
      {error && <div style={{ color: "red" }}>{error}</div>}

      {/* TIMELINE */}
      <div
        style={{
          position: "relative",
          marginTop: 20,
          marginLeft: 40,
          maxWidth: 280,
        }}
      >
        {Array.from(grouped.entries()).map(([stationCode, rows], index) => {
          const station = stationMap.get(stationCode);

          const arrival = rows.find((r) => r.type === "ARRIVAL");
          const departure = rows.find((r) => r.type === "DEPARTURE");

          const isVisited = index <= lastCompletedIndex;

          return (
            <div
              key={stationCode}
              ref={(el) => {
                rowRefs.current[stationCode] = el;
              }}
              style={{
                display: "flex",
                alignItems: "flex-start",
                marginBottom: 2,
                gap: 20,
                borderRadius: "2px",
                background: isVisited
                  ? "rgba(255,255,255,0.05)"
                  : "transparent",
                borderLeft: isVisited ? "2px solid turquoise" : "none",
              }}
            >
              {/* LEFT LINE */}
              <div style={{ width: 2 }} />

              {/* CONTENT */}
              <div style={{ fontSize: 12, minWidth: 220 }}>
                {/* ARRIVAL */}
                <div style={{ marginTop: 4 }}>
                  <TimeRow
                    label={
                      <>
                        {station?.stationName ?? stationCode}
                        <span style={{ color: "gray", marginLeft: 6 }}>
                          {stationCode}
                        </span>
                      </>
                    }
                    scheduled={arrival?.scheduledTime}
                    actual={arrival?.actualTime}
                    track={arrival?.commercialTrack}
                  />
                </div>

                {/* DEPARTURE */}
                <div
                  style={{
                    marginBottom: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <TimeRow
                    scheduled={departure?.scheduledTime}
                    actual={departure?.actualTime}
                    track={index === 0 ? departure?.commercialTrack : undefined}
                    label={
                      index === 0
                        ? `${station?.stationName ?? stationCode} ${stationCode}`
                        : undefined
                    }
                  />

                  {/* viimeisen toteutuneen aseman ero */}
                  {index === lastArrivalIndex && lastArrivalDiff !== null && (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: "bold",
                        marginTop: 2,
                        marginLeft: "12px",
                        color:
                          lastArrivalDiff <= 0 ? "#2ecc71" : "rgb(211,130,146)",
                      }}
                    >
                      {lastArrivalDiff > 0
                        ? `+${lastArrivalDiff} min`
                        : `${lastArrivalDiff} min`}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TrainHistoryView;
